import { Component, type ErrorInfo, type ReactNode } from 'react'
import { isChunkLoadError, reloadForUpdate } from '@/lib/appUpdate'
import { reportError } from '@/lib/monitoring'
import { ErrorState } from '@/components/common/ErrorState'

interface AppErrorBoundaryProps {
  children: ReactNode
  resetKey?: string
}

interface AppErrorBoundaryState {
  failed: boolean
  outdated?: boolean
  failedAt?: string
}

export class AppErrorBoundary extends Component<AppErrorBoundaryProps, AppErrorBoundaryState> {
  state: AppErrorBoundaryState = { failed: false }

  static getDerivedStateFromError(error: unknown): Partial<AppErrorBoundaryState> {
    return { failed: true, outdated: isChunkLoadError(error) }
  }

  static getDerivedStateFromProps(props: AppErrorBoundaryProps, state: AppErrorBoundaryState): Partial<AppErrorBoundaryState> | null {
    if (state.failed && state.failedAt !== undefined && state.failedAt !== (props.resetKey ?? '')) return { failed: false, outdated: false, failedAt: undefined }
    return null
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    this.setState({ failedAt: this.props.resetKey ?? '' })
    if (isChunkLoadError(error) && reloadForUpdate()) return
    reportError(error, info.componentStack)
  }

  reset = () => this.setState({ failed: false, outdated: false, failedAt: undefined })

  render() {
    if (!this.state.failed) return this.props.children
    if (this.state.outdated) {
      return (
        <ErrorState
          title="O Habitar foi atualizado"
          description="Recarregue a página para continuar na versão mais recente."
          retryLabel="Recarregar página"
          onRetry={() => location.reload()}
        />
      )
    }
    return (
      <ErrorState
        title="Algo deu errado nesta tela"
        description="O erro foi registrado. Tente de novo; se continuar, recarregue a página."
        onRetry={this.reset}
      />
    )
  }
}
